import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { RootState } from "src/common/state/store";
import {
  fetchAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  fetchConversations,
  fetchConversation,
  deleteConversation,
  RagAgent,
  RagConversation,
  RagConversationSummary,
} from "../api/ragApi";

type CreateAgentData = Parameters<typeof createAgent>[0];
type UpdateAgentData = Parameters<typeof updateAgent>[1];

type RagAgentsStatus = "idle" | "loading" | "loaded" | "error";

// Same page size as Scenar IA's sidebar (chatbotSlice), so both histories
// scroll identically.
const RAG_CONVERSATIONS_PAGE_SIZE = 20;

interface IRagConversationsPagination {
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface IRagState {
  agents: RagAgent[];
  status: RagAgentsStatus;
  error: string | null;
  /** Sidebar rows (summaries — no message bodies). */
  conversations: RagConversationSummary[];
  conversationsPagination: IRagConversationsPagination;
  conversationsStatus: RagAgentsStatus;
  /** The thread the chat pane is currently showing; null = unsaved new thread. */
  activeConversationId: number | null;
  /** Full thread most recently opened from the sidebar, for the hook to hydrate from. */
  activeConversation: RagConversation | null;
}

const initialState: IRagState = {
  agents: [],
  status: "idle",
  error: null,
  conversations: [],
  conversationsPagination: {
    hasMore: true,
    isLoadingMore: false,
  },
  conversationsStatus: "idle",
  activeConversationId: null,
  activeConversation: null,
};

// Cached fetch: dedupes so "knowledge base" pickers can dispatch it on every
// mount without firing duplicate requests. Skips if a fetch is in flight or the
// list is already fresh.
export const fetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/fetchAgents",
  async () => fetchAgents(),
  {
    condition: (_, { getState }) => {
      const status = getState().rag.status;
      return status !== "loading" && status !== "loaded";
    },
  },
);

// Forced fetch: always hits the server, ignoring the cache. Used after a
// mutation and by the management list, which always wants authoritative state.
export const refetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/refetchAgents",
  async () => fetchAgents(),
);

// Agent mutations all funnel through these thunks so the single canonical
// agents list (state.rag.agents) is re-synced from the server right after the
// change. Every consumer — the management list AND the "knowledge base" pickers
// — reads that one list, so a newly created/edited/deleted agent shows up
// everywhere instantly, with correct server-computed counts and is_shared flag.

export const createAgentThunk = createAsyncThunk<
  RagAgent,
  CreateAgentData,
  { state: RootState; rejectValue: string }
>("rag/createAgent", async (data, { dispatch, rejectWithValue }) => {
  try {
    const agent = await createAgent(data);
    await dispatch(refetchAgentsThunk());
    return agent;
  } catch (err: any) {
    // Forward the backend message (e.g. "Agent limit reached") so the form can
    // map it to a user-facing alert; .unwrap() rethrows this string.
    return rejectWithValue(err?.response?.data?.error?.message || "");
  }
});

export const updateAgentThunk = createAsyncThunk<
  RagAgent,
  { id: number; data: UpdateAgentData },
  { state: RootState; rejectValue: string }
>("rag/updateAgent", async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const agent = await updateAgent(id, data);
    await dispatch(refetchAgentsThunk());
    return agent;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error?.message || "");
  }
});

export const deleteAgentThunk = createAsyncThunk<number, number, { state: RootState }>(
  "rag/deleteAgent",
  async (id, { dispatch }) => {
    await deleteAgent(id);
    await dispatch(refetchAgentsThunk());
    return id;
  },
);

// --- Conversation history (Wixarpedia sidebar) ---

export const fetchConversationsThunk = createAsyncThunk<
  { data: RagConversationSummary[]; total: number },
  void,
  { state: RootState }
>("rag/fetchConversations", async () => fetchConversations(0, RAG_CONVERSATIONS_PAGE_SIZE));

export const fetchMoreConversationsThunk = createAsyncThunk<
  { data: RagConversationSummary[]; total: number },
  { start: number },
  { state: RootState }
>("rag/fetchMoreConversations", async ({ start }) =>
  fetchConversations(start, RAG_CONVERSATIONS_PAGE_SIZE),
);

export const openConversationThunk = createAsyncThunk<
  RagConversation,
  number,
  { state: RootState }
>("rag/openConversation", async (conversationId) => fetchConversation(conversationId));

export const deleteConversationThunk = createAsyncThunk<number, number, { state: RootState }>(
  "rag/deleteConversation",
  async (conversationId) => {
    await deleteConversation(conversationId);
    return conversationId;
  },
);

export const ragSlice = createSlice({
  name: "rag",
  initialState,
  reducers: {
    invalidateAgents: (state) => {
      state.status = "idle";
    },
    /** "Nouvelle requête": drop the active thread so the next ask opens a new one. */
    startNewConversation: (state) => {
      state.activeConversationId = null;
      state.activeConversation = null;
    },
    /**
     * Adopts the id the backend minted for a thread that started unsaved. Only
     * the id changes — `activeConversation` stays null, because the live messages
     * already sit in the chat hook and re-hydrating from the server here would
     * wipe the streaming turn out from under the user.
     */
    setActiveConversationId: (state, { payload }: { payload: number }) => {
      state.activeConversationId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConversationsThunk.pending, (state) => {
      state.conversationsStatus = "loading";
    });
    builder.addCase(fetchConversationsThunk.fulfilled, (state, { payload }) => {
      state.conversations = payload.data;
      state.conversationsStatus = "loaded";
      state.conversationsPagination = {
        hasMore: payload.data.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchConversationsThunk.rejected, (state) => {
      state.conversationsStatus = "error";
      state.conversationsPagination.isLoadingMore = false;
    });

    builder.addCase(fetchMoreConversationsThunk.pending, (state) => {
      state.conversationsPagination.isLoadingMore = true;
    });
    builder.addCase(fetchMoreConversationsThunk.fulfilled, (state, { payload }) => {
      // De-dupe on id: a thread whose updated_at changed between page fetches can
      // shift across the page boundary and arrive twice.
      const known = new Set(state.conversations.map((c) => c.id));
      state.conversations.push(...payload.data.filter((c) => !known.has(c.id)));
      state.conversationsPagination = {
        hasMore: state.conversations.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchMoreConversationsThunk.rejected, (state) => {
      state.conversationsPagination.isLoadingMore = false;
    });

    builder.addCase(openConversationThunk.fulfilled, (state, { payload }) => {
      state.activeConversation = payload;
      state.activeConversationId = payload.id;
    });

    builder.addCase(deleteConversationThunk.fulfilled, (state, { payload }) => {
      state.conversations = state.conversations.filter((c) => c.id !== payload);
      // Deleting the thread you're reading empties the pane rather than leaving
      // it pointing at a row that now 404s.
      if (state.activeConversationId === payload) {
        state.activeConversationId = null;
        state.activeConversation = null;
      }
    });

    // Both the cached and forced fetch resolve into the same canonical list.
    builder
      .addMatcher(isAnyOf(fetchAgentsThunk.pending, refetchAgentsThunk.pending), (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addMatcher(
        isAnyOf(fetchAgentsThunk.fulfilled, refetchAgentsThunk.fulfilled),
        (state, { payload }) => {
          state.agents = payload;
          state.status = "loaded";
        },
      )
      .addMatcher(
        isAnyOf(fetchAgentsThunk.rejected, refetchAgentsThunk.rejected),
        (state, { error }) => {
          state.status = "error";
          state.error = error.message ?? "Failed to fetch RAG agents";
        },
      );
  },
});

export const ragReducer = ragSlice.reducer;
export const { invalidateAgents, startNewConversation, setActiveConversationId } = ragSlice.actions;

export const getRagAgents = (state: RootState) => state.rag.agents;
export const getRagAgentsStatus = (state: RootState) => state.rag.status;

export const getRagConversations = (state: RootState) => state.rag.conversations;
export const getRagConversationsPagination = (state: RootState) =>
  state.rag.conversationsPagination;
export const getRagConversationsStatus = (state: RootState) => state.rag.conversationsStatus;
export const getActiveRagConversationId = (state: RootState) => state.rag.activeConversationId;
export const getActiveRagConversation = (state: RootState) => state.rag.activeConversation;
